import { createContext, useState, useMemo, useEffect, useRef, useCallback } from 'react';
import * as t from 'ts-schema';
import Raven from 'raven-js';
import { createComment, deleteComment, fetchComments, updateComment } from '../apis/CommentingApi';
import { AT_MENTIONED_USER_IDS, COMMENT, DEEP_COMMENT, MESSAGE, META_DESCRIPTION, REPLACE, REPLY } from '../types/CommentTypes';
import { FAILED, PENDING, SUCCEEDED, UNINITIALIZED } from '../types/RequestStatus';
import { CommentDetailSchema, FetchCommentsResponseSchema } from '../types/schemas/CommentsSchema';
import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { isEngagementTypeId } from 'customer-data-objects/engagement/isEngagementTypeId';
import { useOnCrmMessageTopic } from 'crm-message-bus/useOnCrmMessageTopic';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { CommentsUseStyleContext } from './CommentsUseStyleContext';
import { jsx as _jsx } from "react/jsx-runtime";
export const CommentsContext = /*#__PURE__*/createContext({});
export const CommentsContextProvider = props => {
  const {
    children,
    objectId,
    objectType,
    autoScrollToCommentId,
    highlightCommentId,
    shouldDisableReplying,
    commentingNotificationTitle = '',
    commentingNotificationCtaUrl = '',
    owners = [],
    teams,
    isUngatedForTeamMentions = false,
    createCommentCallback,
    onCommentCreated,
    deleteCommentCallback,
    use = 'default',
    oldestFirst = false,
    paginationState: controlledPaginationState,
    onPaginationChange,
    density = 'default',
    commentLayoutVariant = 'default',
    renderToggleRepliesButton,
    hoverBackgroundColor = 'fill-tertiary-hover',
    editSubmitBtnText,
    newCommentButtonText,
    avatarConfig: avatarConfigProp
  } = props;
  const avatarConfig = useMemo(() => {
    var _avatarConfigProp$siz, _avatarConfigProp$flu;
    return {
      size: (_avatarConfigProp$siz = avatarConfigProp === null || avatarConfigProp === void 0 ? void 0 : avatarConfigProp.size) !== null && _avatarConfigProp$siz !== void 0 ? _avatarConfigProp$siz : 'xs',
      flushInline: (_avatarConfigProp$flu = avatarConfigProp === null || avatarConfigProp === void 0 ? void 0 : avatarConfigProp.flushInline) !== null && _avatarConfigProp$flu !== void 0 ? _avatarConfigProp$flu : false
    };
  }, [avatarConfigProp === null || avatarConfigProp === void 0 ? void 0 : avatarConfigProp.size, avatarConfigProp === null || avatarConfigProp === void 0 ? void 0 : avatarConfigProp.flushInline]);
  const [comments, setComments] = useState([]);
  const [isCommenting, setIsCommenting] = useState(false);
  const [fetchStatus, setFetchStatus] = useState(UNINITIALIZED);
  const [afterCursor, setAfterCursor] = useState(undefined);
  const fetchLockRef = useRef(false);
  const objectIdRef = useRef(objectId);
  objectIdRef.current = objectId;
  const [internalPaginationState, setInternalPaginationState] = useState({
    currentPage: 1,
    pageSize: 10,
    expandedThreadIds: new Set()
  });
  const isControlled = controlledPaginationState !== undefined;
  const paginationState = isControlled ? controlledPaginationState : internalPaginationState;
  const setPaginationState = useMemo(() => {
    if (isControlled) {
      return newState => {
        const resolvedState = typeof newState === 'function' ? newState(paginationState) : newState;
        onPaginationChange === null || onPaginationChange === void 0 || onPaginationChange(resolvedState);
      };
    }
    return setInternalPaginationState;
  }, [isControlled, paginationState, onPaginationChange]);
  const sendCommentMessage = useSendCrmMessageTopic(TOPIC_NAMES.COMMENTS_UPDATED);
  const handleSendCommentMessage = useCallback(() => {
    sendCommentMessage({
      objectId,
      objectTypeId: normalizeTypeId(objectType)
    });
  }, [objectId, objectType, sendCommentMessage]);
  const handleRefetchComments = ({
    objectId: refetchObjectId,
    objectTypeId
  }) => {
    if (!isEngagementTypeId(objectTypeId)) {
      if (objectTypeId !== normalizeTypeId(objectType)) {
        return;
      }
    }
    if (refetchObjectId === objectId) {
      setAfterCursor(undefined);
      setFetchStatus(UNINITIALIZED);
    }
  };
  useOnCrmMessageTopic(TOPIC_NAMES.COMMENTS_UPDATED, handleRefetchComments, {
    ignoreLocal: true
  });
  useEffect(() => {
    if (fetchStatus === UNINITIALIZED) {
      setFetchStatus(PENDING);
      fetchComments(objectId, objectType, oldestFirst).then(results => {
        var _validatedCommentsRes;
        const validatedCommentsResponse = t.assert(FetchCommentsResponseSchema, results);
        if (!validatedCommentsResponse) {
          throw new Error('type error on fetchComments API response');
        }
        const ascendingResults = oldestFirst ? validatedCommentsResponse.results : [...validatedCommentsResponse.results].reverse();
        setComments(ascendingResults);
        setAfterCursor((_validatedCommentsRes = validatedCommentsResponse.paging) === null || _validatedCommentsRes === void 0 || (_validatedCommentsRes = _validatedCommentsRes.next) === null || _validatedCommentsRes === void 0 ? void 0 : _validatedCommentsRes.after);
        setFetchStatus(SUCCEEDED);
      }).catch(error => {
        error.message = `[CommentsContext|fetchComments]${error.message}`;
        setFetchStatus(FAILED);
        Raven.captureException(error);
      });
    }
  }, [objectId, fetchStatus, objectType, oldestFirst]);
  const hasNextPage = afterCursor !== undefined;
  const fetchNextPage = useCallback(async () => {
    if (!afterCursor || fetchLockRef.current) {
      return;
    }
    fetchLockRef.current = true;
    const requestedObjectId = objectId;
    try {
      var _validated$paging;
      const results = await fetchComments(objectId, objectType, oldestFirst, afterCursor);
      // handle if objectId changes midflight, to avoid stale data
      if (requestedObjectId !== objectIdRef.current) {
        return;
      }
      const validated = t.assert(FetchCommentsResponseSchema, results);
      if (!validated) {
        throw new Error('type error on fetchComments pagination response');
      }
      const ascendingNewPage = oldestFirst ? validated.results : [...validated.results].reverse();
      setComments(prevComments => {
        const existingIds = new Set(prevComments.map(prevComment => prevComment.id));
        const newComments = ascendingNewPage.filter(newComment => !existingIds.has(newComment.id));
        return oldestFirst ? [...prevComments, ...newComments] : [...newComments, ...prevComments];
      });
      setAfterCursor((_validated$paging = validated.paging) === null || _validated$paging === void 0 || (_validated$paging = _validated$paging.next) === null || _validated$paging === void 0 ? void 0 : _validated$paging.after);
    } catch (error) {
      error.message = `[CommentsContext|fetchNextPage]${error.message}`;
      Raven.captureException(error);
    } finally {
      fetchLockRef.current = false;
    }
  }, [afterCursor, objectId, objectType, oldestFirst]);
  const threadedComments = useMemo(() => {
    const commentsMap = new Map();

    // Build a map of all top level comments with their replies
    comments.forEach(comment => {
      // If it's a top level comment, add it to the commentsMap as a new entry with default no replies
      if (comment.type === COMMENT || comment.type === DEEP_COMMENT) {
        commentsMap.set(comment.id, {
          comment,
          replies: []
        });
      } else {
        const commentEntry = commentsMap.get(comment.parentId);
        // This check is needed until commenting does a backfill to remove replies where top level comments have been removed
        if (commentEntry) {
          commentEntry.replies.push(comment);
        }
      }
    });
    const generalComments = [];
    // AnchorComments are comments on an object on the page, e.g. call time
    const anchorComments = [];
    commentsMap.forEach(value => {
      if (value.comment.type === COMMENT) {
        generalComments.push(value);
      } else {
        anchorComments.push(value);
      }
    });
    if (!oldestFirst) {
      generalComments.reverse();
    }
    return {
      generalComments,
      anchorComments
    };
  }, [comments, oldestFirst]);
  const totalThreadCount = threadedComments.generalComments.length + threadedComments.anchorComments.length;
  useEffect(() => {
    if (fetchStatus === SUCCEEDED && hasNextPage && !fetchLockRef.current) {
      const remaining = totalThreadCount - paginationState.currentPage * paginationState.pageSize;
      if (remaining <= paginationState.pageSize) {
        void fetchNextPage();
      }
    }
  }, [fetchStatus, hasNextPage, fetchNextPage, paginationState.currentPage, paginationState.pageSize, totalThreadCount]);
  const handleAddComment = useCallback(payload => {
    return new Promise((resolve, reject) => {
      createComment(payload).then(result => {
        const validatedComment = t.assert(CommentDetailSchema, result);
        if (!validatedComment) {
          throw new Error('type error on createComment API response');
        }
        const newState = [...comments, validatedComment];
        if (createCommentCallback) {
          const isTimestamped = payload.metaDescription && (payload.metaDescription.options.callTimestamp || payload.metaDescription.options.transcriptUtteranceId);
          const mentionsCount = payload.atMentionedUserIds.length;
          const isReplyToThread = payload.type === REPLY;
          createCommentCallback(!!isTimestamped, mentionsCount, isReplyToThread, payload.mentionCounts);
        }
        setComments(newState);
        handleSendCommentMessage();
        if (onCommentCreated) {
          onCommentCreated(payload);
        }
        resolve();
      }).catch(error => {
        error.message = `[CommentsContext|createComment]${error.message}`;
        Raven.captureException(error);
        reject(error);
      });
    });
  }, [comments, createCommentCallback, onCommentCreated, handleSendCommentMessage]);
  const handleUpdateComment = useCallback(({
    commentId,
    message,
    atMentionedUserIds,
    metaDescription
  }) => {
    return new Promise((resolve, reject) => {
      const payload = [];
      if (message) {
        payload.push({
          op: REPLACE,
          path: MESSAGE,
          value: message
        });
      }
      if (atMentionedUserIds) {
        payload.push({
          op: REPLACE,
          path: AT_MENTIONED_USER_IDS,
          value: atMentionedUserIds
        });
      }
      if (metaDescription) {
        payload.push({
          op: REPLACE,
          path: META_DESCRIPTION,
          value: metaDescription
        });
      }
      if (payload.length && commentId) {
        updateComment(commentId, payload).then(result => {
          const validatedComment = t.assert(CommentDetailSchema, result);
          if (!validatedComment) {
            throw new Error('type error on updateComment API response');
          }
          const newState = comments.map(comment => comment.id === commentId ? validatedComment : comment);
          setComments(newState);
          handleSendCommentMessage();
          resolve();
        }).catch(error => {
          error.message = `[CommentsContect|updateComment]${error.message}`;
          Raven.captureException(error);
          reject(error);
        });
      }
    });
  }, [comments, handleSendCommentMessage]);
  const handleDeleteComment = useCallback(commentId => {
    return new Promise((resolve, reject) => {
      deleteComment(commentId).then(() => {
        let isDeletedCommentReply;
        const newState = comments.filter(comment => {
          if (comment.id === commentId) {
            isDeletedCommentReply = comment.type === REPLY;
          }
          return comment.id !== commentId && comment.parentId !== commentId;
        });
        setComments(newState);
        if (deleteCommentCallback && isDeletedCommentReply !== undefined) {
          deleteCommentCallback(isDeletedCommentReply);
        }
        handleSendCommentMessage();
        resolve();
      }).catch(error => {
        error.message = `[CommentsContect|deleteComment]${error.message}`;
        Raven.captureException(error);
        reject(error);
      });
    });
  }, [comments, deleteCommentCallback, handleSendCommentMessage]);

  // Our instance of RTE Editor uses ownerIds since notes (engagements) uses atMentionedOwnerIds.
  // However, commenting uses atMentionedUserIds for PATCH and GET so we'll to translate between the two when loading the RTE and when sending the requests
  const ownerIdToUserIdMapping = useMemo(() => {
    const ownerIdToUserIdMap = new Map();
    owners.forEach(owner => {
      ownerIdToUserIdMap.set(owner.ownerId, owner.activeUserId);
    });
    return ownerIdToUserIdMap;
  }, [owners]);
  const userIdToOwnerIdMapping = useMemo(() => {
    const userIdToOwnerIdMap = new Map();
    owners.forEach(owner => {
      if (owner.activeUserId) {
        userIdToOwnerIdMap.set(owner.activeUserId, owner.ownerId);
      }
    });
    return userIdToOwnerIdMap;
  }, [owners]);
  const getUserIdsFromOwnerIds = useCallback(ownerIds => {
    const userIds = [];
    ownerIds.forEach(ownerId => {
      const userId = ownerIdToUserIdMapping.get(ownerId);
      if (userId) {
        userIds.push(userId);
      }
    });
    return userIds;
  }, [ownerIdToUserIdMapping]);
  const getOwnerIdsFromUserIds = useCallback(userIds => {
    const ownerIds = [];
    userIds.forEach(userId => {
      const ownerId = userIdToOwnerIdMapping.get(userId);
      if (ownerId) {
        ownerIds.push(ownerId);
      }
    });
    return ownerIds;
  }, [userIdToOwnerIdMapping]);
  const contextValue = useMemo(() => ({
    objectId,
    objectType,
    autoScrollToCommentId,
    shouldDisableReplying: !!shouldDisableReplying,
    commentingNotificationTitle,
    commentingNotificationCtaUrl,
    fetchStatus,
    hasNextPage,
    fetchNextPage,
    generalComments: threadedComments.generalComments,
    anchorComments: threadedComments.anchorComments,
    isCommenting,
    setIsCommenting,
    handleAddComment,
    handleUpdateComment,
    handleDeleteComment,
    highlightCommentId,
    numComments: comments.length,
    currentUserId: props.currentUserId,
    owners: props.owners,
    teams: teams || [],
    isUngatedForTeamMentions,
    getUserIdsFromOwnerIds,
    getOwnerIdsFromUserIds,
    onError: props.onError,
    use,
    paginationState,
    setPaginationState,
    density,
    commentLayoutVariant,
    renderToggleRepliesButton,
    hoverBackgroundColor,
    editSubmitBtnText,
    newCommentButtonText,
    avatarConfig
  }), [objectId, objectType, autoScrollToCommentId, shouldDisableReplying, commentingNotificationTitle, commentingNotificationCtaUrl, fetchStatus, hasNextPage, fetchNextPage, threadedComments, isCommenting, setIsCommenting, handleAddComment, handleUpdateComment, handleDeleteComment, highlightCommentId, comments.length, props.currentUserId, props.owners, teams, isUngatedForTeamMentions, getUserIdsFromOwnerIds, getOwnerIdsFromUserIds, props.onError, use, paginationState, setPaginationState, density, commentLayoutVariant, renderToggleRepliesButton, hoverBackgroundColor, editSubmitBtnText, newCommentButtonText, avatarConfig]);
  return /*#__PURE__*/_jsx(CommentsContext.Provider, {
    value: contextValue,
    children: /*#__PURE__*/_jsx(CommentsUseStyleContext.Provider, {
      value: use,
      children: children
    })
  });
};